import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FileUploadComponent } from '../upload/file-upload/file-upload.component';
import { PpwrDocumentDto, PpwrDocumentSource, PpwrDocumentType } from 'src/app/data-transfer/entities/doc-entities/ppwr-document-dto';
import { FileApiService } from 'src/app/data-transfer/services/file-api-service';
import { TranslocoService } from '@jsverse/transloco';
import { MatDialog } from '@angular/material/dialog';
import { DeleteDialogData, DeleteItemDialogComponent } from 'src/app/components/dialogs/delete-item-dialog/delete-item-dialog.component';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { Observable, Subscription, concat, of } from 'rxjs';
import { concatMap, tap, toArray } from 'rxjs/operators';
import { SimpleDialogData } from '../../dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SimpleConfirmDialogComponent } from '../../dialogs/simple-confirm-dialog/simple-confirm-dialog.component';

interface PendingPpwrDocumentChange {
  persistedDocumentId: number;
  persistedOriginalDocumentId?: number | null;
  file?: File;
}

@Component({
  selector: 'app-ppwr-documents',
  templateUrl: './ppwr-documents.component.html',
  styleUrls: ['./ppwr-documents.component.scss'],
})
export class PpwrDocumentsComponent extends FileUploadComponent implements OnInit, OnDestroy {
  @Input() documents: PpwrDocumentDto[] = [];
  @Output() documentsChange = new EventEmitter<PpwrDocumentDto[]>();
  @Input() canReplaceDocuments = false;

  PpwrDocumentType = PpwrDocumentType;
  PpwrDocumentSource = PpwrDocumentSource;

  readonly documentTypeOptions = [
    {
      value: PpwrDocumentType.DoC,
      labelKey: 'ppwrDocuments.typeDoc',
      cssClass: 'doc',
    },
    {
      value: PpwrDocumentType.TD,
      labelKey: 'ppwrDocuments.typeTd',
      cssClass: 'td',
    },
  ];

  newFiles: File[] = [];

  private documentToReplace?: PpwrDocumentDto;
  private idSubscription?: Subscription;
  private readonly removedDocumentIds: number[] = [];
  private readonly pendingChanges = new Map<PpwrDocumentDto, PendingPpwrDocumentChange>();

  constructor(
    protected override dialog: MatDialog,
    private readonly fileApiService: FileApiService,
    private readonly translateService: TranslocoService
  ) {
    super(dialog);
  }

  ngOnInit(): void {
    super.setInitialNamesLength();
    super.setExistingNamesLength(this.documents);
  }

  onFileSelected(event: Event, documentType: PpwrDocumentType): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (file.type !== 'application/pdf') {
      this.showDialog({
        title: file.name,
        messages: [this.translateService.translate('errors.formatNotSupported')],
        icon: 'error',
      });
      return;
    }

    let assignedName = file.name;
    const listOfNames = this.documents.map(d => d.name);
    if (listOfNames.includes(file.name)) {
      assignedName = this.appendTextToDuplicateNameRecursively(listOfNames, file.name, 0);
      this.newFiles.push(new File([file], assignedName, { type: file.type }));
    } else {
      this.newFiles.push(file);
    }

    const position = this.documents.length;
    this.resizeInput(position);
    this.documents.push({
      index: position,
      name: assignedName,
      documentType,
      source: PpwrDocumentSource.UserUploaded,
      documentId: 0,
      description: '',
    });

    this.documentsChange.emit(this.documents);
    input.value = '';
    this.filesEdited.emit(true);
  }

  onNameEditClicked(index: number): void {
    super.onNameEdited(this.documents, index, this.getDuplicateNamesDialogData());
  }

  onDocumentTypeChange(ppwrdoc: PpwrDocumentDto, newType: PpwrDocumentType): void {
    if (ppwrdoc.documentType === newType) return;

    ppwrdoc.documentType = newType;
    this.documentsChange.emit(this.documents);
    this.filesEdited.emit(true);
  }

  getDocumentTypeLabel(type: PpwrDocumentType): string {
    const option = this.documentTypeOptions.find(option => option.value === type)!;
    return this.translateService.translate(option.labelKey);
  }

  getDocumentTypeCssClass(type: PpwrDocumentType): string {
    return this.documentTypeOptions.find(option => option.value === type)!.cssClass;
  }

  private getDuplicateNamesDialogData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.warning'),
      messages: [this.translateService.translate('warnings.duplicatePdfNames')],
      icon: 'warning',
    };
  }

  onViewDocumentClick(ppwrdoc: PpwrDocumentDto): void {
    const replacementFile = this.pendingChanges.get(ppwrdoc)?.file;
    if (replacementFile) {
      const url = window.URL.createObjectURL(replacementFile);
      window.open(url, '_blank');
    } else if (ppwrdoc.documentId === 0) {
      const file = this.newFiles.find(f => f.name === ppwrdoc.name);
      if (!file) return;
      const url = window.URL.createObjectURL(file);
      window.open(url, '_blank');
    } else {
      this.idSubscription = this.fileApiService.getPdfById(ppwrdoc.documentId).subscribe(blob => {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      });
    }
  }

  onDeleteClick(ppwrdoc: PpwrDocumentDto): void {
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translateService.translate('ppwrDocuments.deleteHeader'),
      dialogText: this.translateService.translate('ppwrDocuments.deleteText'),
      confirmButtonLabel: this.translateService.translate('ppwrDocuments.deleteConfirmButton'),
    };

    this.dialogSubscription = this.dialog
      .open(DeleteItemDialogComponent, getDialogConfig(dialogData, '500px'))
      .afterClosed()
      .subscribe(result => {
        if (result.event === DialogActions.DELETE) {
          this.deleteDocument(ppwrdoc);
        }
      });
  }

  canUpdateDocument(ppwrdoc: PpwrDocumentDto): boolean {
    return this.canEditForm && this.canReplaceDocuments && ppwrdoc.source === PpwrDocumentSource.PacoGenerated && ppwrdoc.id != null && ppwrdoc.documentId !== 0;
  }

  canRevertDocument(ppwrdoc: PpwrDocumentDto): boolean {
    return this.canUpdateDocument(ppwrdoc) && ppwrdoc.originalDocumentId != null;
  }

  onUpdateClick(ppwrdoc: PpwrDocumentDto, replaceInput: HTMLInputElement): void {
    this.documentToReplace = ppwrdoc;
    replaceInput.click();
  }

  onReplaceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const ppwrdoc = this.documentToReplace;
    this.documentToReplace = undefined;
    if (!input.files?.length || !ppwrdoc) return;

    const file = input.files[0];
    input.value = '';
    if (file.type !== 'application/pdf') {
      this.showDialog({
        title: file.name,
        messages: [this.translateService.translate('errors.formatNotSupported')],
        icon: 'error',
      });
      return;
    }

    const pendingChange = this.getOrCreatePendingChange(ppwrdoc);
    pendingChange.file = file;
    ppwrdoc.originalDocumentId ??= ppwrdoc.documentId;
    this.documentsChange.emit(this.documents);
  }

  onRevertClick(ppwrdoc: PpwrDocumentDto): void {
    const dialogData: SimpleDialogData = {
      title: this.translateService.translate('ppwrDocuments.revertHeader'),
      messages: [this.translateService.translate('ppwrDocuments.revertText')],
      icon: 'warning',
    };

    this.dialogSubscription = this.dialog
      .open(SimpleConfirmDialogComponent, getDialogConfig(dialogData, '500px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event === DialogActions.CONFIRM) {
          this.revertDocument(ppwrdoc);
        }
      });
  }

  private revertDocument(ppwrdoc: PpwrDocumentDto): void {
    if (ppwrdoc.originalDocumentId == null) {
      return;
    }
    const pendingChange = this.getOrCreatePendingChange(ppwrdoc);
    pendingChange.file = undefined;
    ppwrdoc.documentId = ppwrdoc.originalDocumentId;
    ppwrdoc.originalDocumentId = null;
    // reverting a replacement that was never saved only cancels it
    if (ppwrdoc.documentId === pendingChange.persistedDocumentId && pendingChange.persistedOriginalDocumentId == null) {
      this.pendingChanges.delete(ppwrdoc);
    }
    this.documentsChange.emit(this.documents);
  }

  private getOrCreatePendingChange(ppwrdoc: PpwrDocumentDto): PendingPpwrDocumentChange {
    let pendingChange = this.pendingChanges.get(ppwrdoc);
    if (!pendingChange) {
      pendingChange = { persistedDocumentId: ppwrdoc.documentId, persistedOriginalDocumentId: ppwrdoc.originalDocumentId };
      this.pendingChanges.set(ppwrdoc, pendingChange);
    }
    return pendingChange;
  }

  private deleteDocument(ppwrdoc: PpwrDocumentDto): void {
    if (ppwrdoc.documentId === 0) {
      this.newFiles = this.newFiles.filter(f => f.name !== ppwrdoc.name);
    }
    this.pendingChanges.delete(ppwrdoc);
    if (ppwrdoc.id != null) {
      this.removedDocumentIds.push(ppwrdoc.id);
    }
    this.documents = this.documents.filter(d => d !== ppwrdoc);
    this.documentsChange.emit(this.documents);
    this.filesEdited.emit(true);
  }

  public getRemovedDocumentIds(): number[] {
    return [...this.removedDocumentIds];
  }

  public getNewPpwrFilesFormData(): FormData {
    return super.getNewFilesFormData(this.newFiles, 'files');
  }

  public updateDocumentIdsAfterSaving(docIds: number[]): void {
    for (let i = 0; i < docIds.length; i++) {
      if (this.newFiles[i]) {
        const doc = this.documents.find(d => d.name === this.newFiles[i].name);
        if (doc) doc.documentId = docIds[i];
      }
    }
    this.newFiles = [];
  }

  public hasPendingChanges(): boolean {
    return this.pendingChanges.size > 0;
  }

  // Uploads the replacement files and then sends the replace/restore requests. Uploaded and completed changes are
  // written back immediately, so retrying after a failed save neither uploads nor sends them again.
  public getPendingChangesRequest(
    replaceFunction: (ppwrDocumentId: number, documentId: number) => Observable<void>,
    restoreFunction: (ppwrDocumentId: number) => Observable<void>
  ): Observable<unknown> {
    const replacements = [...this.pendingChanges.entries()].filter(([, change]) => change.file != null);
    const upload =
      replacements.length > 0
        ? this.fileApiService
            .putDocuments(
              this.getNewFilesFormData(
                replacements.map(([, change]) => change.file!),
                'files'
              ),
              true
            )
            .pipe(tap(ids => this.applyUploadedReplacementIds(replacements, ids)))
        : of([]);

    return upload.pipe(
      concatMap(() =>
        concat(
          ...[...this.pendingChanges.keys()].map(doc =>
            (doc.originalDocumentId == null ? restoreFunction(doc.id!) : replaceFunction(doc.id!, doc.documentId)).pipe(tap(() => this.pendingChanges.delete(doc)))
          )
        )
      ),
      toArray()
    );
  }

  private applyUploadedReplacementIds(replacements: [PpwrDocumentDto, PendingPpwrDocumentChange][], ids: number[]): void {
    if (ids?.length !== replacements.length || ids.some(id => !Number.isInteger(id) || id <= 0)) {
      throw new Error('Invalid document upload response for PPWR document replacements');
    }
    replacements.forEach(([doc, change], i) => {
      doc.documentId = ids[i];
      change.file = undefined;
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.idSubscription?.unsubscribe();
  }
}
